import React from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormFields, FormTextField } from "sprint-common-web";

import { DeliveryEndpointType } from "../../../../common/types/DeliveryEndpointType";
import { validateAsperaKey } from "../../common/validateAsperaKey";
import { AsperaDeliveryEndpointDetails, DeliveryEndpointDetails } from "../../listing/DeliveryEndpointItem";
import { useEditAsperaDeliveryEndpointForm } from "./useEditAsperaDeliveryEndpointForm";

export interface EditAsperaDeliveryEndpointFormProps {
    onCancel(): void;
    onSubmit(): void;
    deliveryEndpointId: string;
}

export interface EditAsperaDeliveryEndpointFormInputs {
    name: string;
    type: DeliveryEndpointType.Aspera;
    url: string;
    user: string;
    ssmPrivateKeyName: string;
    workspace?: string;
}

export const EditAsperaDeliveryEndpointForm: React.VFC<EditAsperaDeliveryEndpointFormProps> = ({
    deliveryEndpointId,
    onSubmit,
    onCancel
}: EditAsperaDeliveryEndpointFormProps) => {
    const { deliveryEndpoint, editDeliveryEndpoint } = useEditAsperaDeliveryEndpointForm(deliveryEndpointId);

    ensureAsperaDetails(deliveryEndpoint.details);

    const { errors, control, formState, reset, handleSubmit } = useForm<EditAsperaDeliveryEndpointFormInputs>({
        defaultValues: {
            name: deliveryEndpoint.name,
            url: deliveryEndpoint.details.url,
            user: deliveryEndpoint.details.user,
            ssmPrivateKeyName: deliveryEndpoint.details.ssmPrivateKeyName,
            workspace: deliveryEndpoint.details.workspace
        },
        criteriaMode: "all"
    });

    const handleEditDeliveryEndpoint = async (inputs: EditAsperaDeliveryEndpointFormInputs) => {
        await editDeliveryEndpoint(inputs);
        onSubmit();
    };

    const handleCancelButtonClick = () => {
        reset();
        onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleEditDeliveryEndpoint)}>
            <FormFields>
                <FormTextField
                    label="Name"
                    control={control}
                    name="name"
                    errors={errors}
                    rules={{ required: "Enter name" }}
                />
                <FormTextField
                    label="Url"
                    control={control}
                    name="url"
                    errors={errors}
                    rules={{ required: "Enter url" }}
                />
                <FormTextField
                    label="User"
                    control={control}
                    name="user"
                    errors={errors}
                    rules={{ required: "Enter user" }}
                />
                <FormTextField
                    label="Smm private key name"
                    control={control}
                    name="ssmPrivateKeyName"
                    errors={errors}
                    rules={{
                        validate: validateAsperaKey,
                        required: "Enter ssm private key name"
                    }}
                />
                <FormTextField label="Workspace" control={control} name="workspace" errors={errors} />
            </FormFields>
            <FormActions
                isLoading={formState.isSubmitting || formState.isValidating}
                onCancel={handleCancelButtonClick}
            />
        </Form>
    );
};

const ensureAsperaDetails: (value: DeliveryEndpointDetails) => asserts value is AsperaDeliveryEndpointDetails = (
    value
) => {
    if (value.type !== DeliveryEndpointType.Aspera) {
        throw Error("Delivery endpoint should be of type 'aspera'");
    }
};
